import { Link } from "react-router-dom";
import { Check, Sparkles, ArrowUpRight, Shield } from "lucide-react";
import PublicLayout from "../../components/public/PublicLayout";

const TIERS = [
  {
    id: "starter",
    name: "Starter",
    price: "₹4,999",
    period: "/month",
    annual: "₹49,990/year · save 2 months",
    tagline: "For solo IFAs just getting started",
    highlight: false,
    features: [
      "Up to 250 clients",
      "Up to ₹25 Cr AUM",
      "PAN → Folio → Scheme drill-down",
      "Daily NAV + AMFI category sync",
      "1 email mailbox (Karvy + CAMS auto-ingest)",
      "1 user / RM seat",
      "Email support (next business day)",
    ],
    cta: { label: "Start free trial", to: "/start-trial?plan=starter" },
  },
  {
    id: "pro",
    name: "Pro",
    price: "₹8,999",
    period: "/month",
    annual: "₹89,990/year · save 2 months",
    tagline: "For growing sub-brokers and MFD teams",
    highlight: true,
    features: [
      "Up to 1,000 clients",
      "Up to ₹150 Cr AUM",
      "Everything in Starter",
      "Family Mode + Family AUM rollup",
      "Profit Realignment + Exit-Free Lots",
      "Unpaid Brokerage reconciliation (WBR77 + MFSD205)",
      "KYC Mismatch reports + Reinvestment audit",
      "Bulk PDF / Excel reports for clients",
      "3 mailboxes, up to 5 user / RM seats",
      "Priority email + chat support",
    ],
    cta: { label: "Start free trial", to: "/start-trial?plan=pro" },
  },
  {
    id: "enterprise",
    name: "Enterprise",
    price: "₹16,999",
    period: "/month",
    annual: "Custom annual quote",
    tagline: "For larger AMCs and multi-RM teams",
    highlight: false,
    features: [
      "Unlimited clients",
      "Unlimited AUM",
      "Everything in Pro",
      "White-label: your domain, your logo, your colors",
      "Watermarked PDF reports + custom letterheads",
      "Dedicated onboarding manager",
      "Custom integrations (Razorpay, Zoho, Calendly, …)",
      "4-hour SLA + WhatsApp support",
      "10+ user / RM seats",
      "Annual data audit & SEBI-grade retention",
    ],
    cta: { label: "Book a private walkthrough", to: "/demo?plan=enterprise" },
  },
];

export default function Pricing() {
  return (
    <PublicLayout title="Pricing">
      <div data-testid="pricing-page">
        {/* Hero */}
        <section className="relative pt-16 pb-12 px-6 sm:px-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-white/40 mb-6">
              <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
              Built for ARN holders
              <span className="h-[1px] w-8 bg-[#C8A96B]/60" />
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-[-0.02em] leading-[1.1]">
              Stop juggling spreadsheets.
              <br />
              <span className="italic text-[#C8A96B]">Start running your firm.</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-white/55 font-light leading-[1.7] max-w-2xl mx-auto">
              Everything Kinntegra runs on — Karvy &amp; CAMS auto-ingest, PAN-grouped drill-downs,
              brokerage reconciliation, KYC mismatch — now available as a white-label SaaS for your practice.
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#C8A96B] border border-[#C8A96B]/30 bg-[#C8A96B]/[0.05] rounded-full px-4 py-1.5">
              <Shield size={14} /> 14-day free trial · no credit card · cancel anytime
            </div>
          </div>
        </section>

        {/* Tiers */}
        <section className="px-6 sm:px-10 pb-20">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <div
                key={t.id}
                data-testid={`pricing-tier-${t.id}`}
                className={`relative rounded-2xl p-6 backdrop-blur-md transition-all ${
                  t.highlight
                    ? "border-2 border-[#C8A96B] bg-gradient-to-b from-[#C8A96B]/[0.08] to-transparent shadow-[0_0_60px_-15px_rgba(200,169,107,0.25)] scale-[1.02]"
                    : "border border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
                }`}
              >
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8A96B] text-[#07111F] text-xs font-medium tracking-wide px-4 py-1 rounded-full flex items-center gap-1.5">
                    <Sparkles size={12} /> Most popular
                  </div>
                )}
                <div className="text-xs uppercase tracking-[0.2em] text-white/40">{t.tagline}</div>
                <h3 className="text-3xl font-light mt-2 tracking-tight">{t.name}</h3>
                <div className="mt-5">
                  <span className="text-4xl font-light tracking-tight text-white">{t.price}</span>
                  <span className="text-white/45 ml-1">{t.period}</span>
                </div>
                <div className="text-xs text-white/40 mt-1">{t.annual}</div>
                <ul className="space-y-2.5 mt-7 text-sm">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex gap-2 text-white/75">
                      <Check size={15} className="text-[#C8A96B] shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={t.cta.to}
                  data-testid={`pricing-cta-${t.id}`}
                  className={`mt-7 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-medium transition ${
                    t.highlight
                      ? "bg-[#C8A96B] text-[#07111F] hover:bg-[#d4b878]"
                      : "border border-white/20 text-white/85 hover:bg-white/[0.06]"
                  }`}
                >
                  {t.cta.label}
                  <ArrowUpRight size={14} />
                </Link>
              </div>
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-14 text-center">
            <p className="text-sm text-white/50 leading-[1.7]">
              All plans include: complete data isolation · daily backups · platform updates rolled out automatically · GST-compliant invoices.
            </p>
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-[#C8A96B]/80">
              <Link to="/for-arn-holders" className="hover:text-[#C8A96B] inline-flex items-center gap-1">Why ARN holders are switching <ArrowUpRight size={14} /></Link>
              <span className="text-white/20">·</span>
              <Link to="/demo" className="hover:text-[#C8A96B] inline-flex items-center gap-1">Book a 1:1 demo <ArrowUpRight size={14} /></Link>
            </div>
          </div>
        </section>
      </div>
    </PublicLayout>
  );
}
